import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback
} from 'react'
import { CurrentUserContext } from '../Shared/CurrentUser.context'
import { CurrentPortalContext } from '../Shared/CurrentPortal.context'

import { getJWT } from '../../utils/functions'

import io from 'socket.io-client'
import { NotificationContext } from '../Shared/Notification.context'

export const ChatSocketContext = createContext(null)

const PUSH_SUBSCRIPTION_REGISTER_EVENT = 'chat:registerPushSubscription'
const PUSH_SUBSCRIPTION_UNREGISTER_EVENT = 'chat:unregisterPushSubscription'

const isPushSupported = () => {
  if (typeof window === 'undefined') {
    return false
  }

  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

const urlBase64ToUint8Array = (base64String) => {
  const normalized = base64String.replace(/-/g, '+').replace(/_/g, '/')
  const padding = '='.repeat((4 - (normalized.length % 4)) % 4)
  const base64 = normalized + padding
  const rawData = window.atob(base64)

  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)))
}

const buildPushSubscriptionPayload = (subscription, userId, portalId) => {
  if (!subscription || !userId || !portalId) {
    return null
  }

  const subscriptionJson = subscription.toJSON()

  return {
    endpoint: subscriptionJson.endpoint,
    expirationTime: subscriptionJson.expirationTime ?? null,
    keys: subscriptionJson.keys || {},
    userId,
    portalId,
    applicationId: 'televu-isee'
  }
}

const getSubscriptionHash = (payload, socketId) => {
  if (!payload) {
    return ''
  }

  return [
    socketId || '',
    payload.userId,
    payload.portalId,
    payload.endpoint,
    payload.keys?.p256dh || '',
    payload.keys?.auth || ''
  ].join(':')
}

const cleanupSocket = (socketInstance) => {
  if (!socketInstance) return
  if (typeof socketInstance.off === 'function') {
    socketInstance.off('chat:getUserChats')
    socketInstance.off('chat:sendMessage')
    socketInstance.off('user:getOnlineChatUsers')
    socketInstance.off('chat:updateLastSeen')
    socketInstance.off('user:connected')
    socketInstance.off('user:disconnected')
    socketInstance.off('user:getOnlinePortalUsers')
    socketInstance.off('chat:chatCreated')
    socketInstance.off('chat:isTyping')
    socketInstance.off('connect')
    socketInstance.off('disconnect')
    socketInstance.off('connect_error')
  }
  if (typeof socketInstance.disconnect === 'function') {
    socketInstance.disconnect()
  }
}

export const ChatSocketContextProvider = ({ userId, portalId, ...props }) => {
  const [socket, setSocket] = useState(null)
  const [chats, setChats] = useState([])
  const [currChatIndex, setCurrChatIndex] = useState(-1)
  const [currChatName, setCurrChatName] = useState('')
  const [onlineUsers, setOnlineUsers] = useState([])
  const [portalUsers, setPortalUsers] = useState([])
  const [portalUsersMap, setPortalUsersMap] = useState({})
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [loadingChats, setLoadingChats] = useState(true)

  const { me } = useContext(CurrentUserContext)
  const { portal, fetchPortalUsers } = useContext(CurrentPortalContext)
  const { setSnackBarMessage } = useContext(NotificationContext)
  const [newMessage, setNewMessage] = useState(null)

  // Use ref to store socket instance for disconnect function
  const socketRef = useRef(null)
  const pushSubscriptionRef = useRef(null)
  const pushSyncRef = useRef({
    socketId: null,
    subscriptionHash: ''
  })
  const ensurePushSubscriptionRef = useRef(null)
  const syncPushSubscriptionRef = useRef(null)

  const syncPushSubscriptionWithBackend = useCallback(
    (socketInstance, subscription, action = 'register') => {
      const payload = buildPushSubscriptionPayload(subscription, me?.id, portalId)

      if (!payload || !socketInstance || typeof socketInstance.emit !== 'function') {
        return false
      }

      const socketId = socketInstance.id || ''
      const subscriptionHash = getSubscriptionHash(payload, socketId)

      if (
        action === 'register' &&
        pushSyncRef.current.socketId === socketId &&
        pushSyncRef.current.subscriptionHash === subscriptionHash
      ) {
        return false
      }

      // TODO(chat-backend): ensure these socket events persist/remove push subscriptions.
      const eventName =
        action === 'register'
          ? PUSH_SUBSCRIPTION_REGISTER_EVENT
          : PUSH_SUBSCRIPTION_UNREGISTER_EVENT

      try {
        socketInstance.emit(eventName, payload)
      } catch (error) {
        console.error(`Failed to emit ${eventName}:`, error)
        return false
      }

      if (action === 'register') {
        pushSyncRef.current = {
          socketId,
          subscriptionHash
        }
        pushSubscriptionRef.current = subscription
      } else {
        pushSyncRef.current = {
          socketId: null,
          subscriptionHash: ''
        }
      }

      return true
    },
    [me?.id, portalId]
  )

  const ensurePushSubscription = useCallback(
    async (socketInstance) => {
      if (!isPushSupported()) {
        return
      }

      if (!socketInstance || !socketInstance.connected) {
        return
      }

      if (Notification.permission !== 'granted') {
        return
      }

      const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY
      if (!vapidPublicKey) {
        return
      }

      try {
        const registration = await navigator.serviceWorker.ready
        let subscription = await registration.pushManager.getSubscription()

        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
          })
        }

        if (!subscription) {
          return
        }

        syncPushSubscriptionWithBackend(socketInstance, subscription, 'register')
      } catch (error) {
        console.error('Failed to initialize web push subscription:', error)
      }
    },
    [syncPushSubscriptionWithBackend]
  )

  const unsubscribeBrowserPushSubscription = useCallback(async () => {
    if (!isPushSupported()) {
      return
    }

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription =
        pushSubscriptionRef.current ||
        (await registration.pushManager.getSubscription())

      if (subscription) {
        await subscription.unsubscribe()
      }
    } catch (error) {
      console.error('Failed to unsubscribe browser push subscription:', error)
    }

    pushSubscriptionRef.current = null
    pushSyncRef.current = {
      socketId: null,
      subscriptionHash: ''
    }
  }, [])

  useEffect(() => {
    syncPushSubscriptionRef.current = syncPushSubscriptionWithBackend
  }, [syncPushSubscriptionWithBackend])

  useEffect(() => {
    ensurePushSubscriptionRef.current = ensurePushSubscription
  }, [ensurePushSubscription])

  useEffect(() => {
    const fetchParticipants = async () => {
      if (!portal?.name || !fetchPortalUsers) {
        return
      }
      try {
        const users = await fetchPortalUsers(portal.name)
        setPortalUsers(users.map((el) => el.user))

        const usersObj = users.reduce((acc, curr) => {
          acc[curr.user.id] = curr.user
          return acc
        }, {})

        setPortalUsersMap(usersObj)
      } catch (error) {
        console.error('Failed to fetch portal users:', error)
      }
    }

    fetchParticipants()
  }, [portalId, portal?.name, fetchPortalUsers])

  useEffect(() => {
    if (!isPushSupported()) {
      return
    }

    const handleServiceWorkerMessage = (event) => {
      if (event?.data?.type !== 'PUSH_SUBSCRIPTION_CHANGED') {
        return
      }

      pushSyncRef.current = {
        socketId: null,
        subscriptionHash: ''
      }

      ensurePushSubscriptionRef.current?.(socketRef.current)
    }

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage)

    return () => {
      navigator.serviceWorker.removeEventListener(
        'message',
        handleServiceWorkerMessage
      )
    }
  }, [])

  useEffect(() => {
    if (!isPushSupported() || !me?.id) {
      return
    }

    if (Notification.permission === 'granted') {
      ensurePushSubscriptionRef.current?.(socketRef.current)
    }
  }, [me?.id])

  const onConnect = useCallback(() => {
    setLoadingChats(false)
    ensurePushSubscriptionRef.current?.(socketRef.current)
  }, [])

  const onDisconnect = useCallback(() => {
    pushSyncRef.current = {
      socketId: null,
      subscriptionHash: ''
    }
  }, [])

  const processChats = (chats) => {
    const processedChats = chats.map((chat) => {
      const unreadMessages = chat.messages?.filter(
        (message) =>
          message.senderId !== me.id &&
          new Date(message.timestamp) > new Date(chat.lastSeen)
      ).length

      let lastMessageTime = null

      if (chat.messages) {
        lastMessageTime = chat.messages[chat.messages?.length - 1]?.timestamp
      }

      return {
        ...chat,
        lastMessageTime,
        unreadMessages
      }
    })

    return processedChats
  }

  const getChatName = (chat, currUserId) => {
    if (chat?.name) return chat.name
    const participant = chat?.participants?.find((p) => p.userId !== currUserId)
    if (participant && participant.userId in portalUsersMap) {
      return portalUsersMap[participant.userId]?.name
    }
    return ''
  }

  useEffect(() => {
    if (!newMessage) return
    if (
      newMessage.senderId !== me.id &&
      chats[currChatIndex]?.id !== newMessage.chatId
    ) {
      setSnackBarMessage(
        `New Chat Recieved\n${getChatName(newMessage.chat, me.id)}: ${
          newMessage.text
        }`,
        'info'
      )
    }
    setNewMessage(null)
  }, [newMessage, chats, currChatIndex, me, portalUsersMap, setSnackBarMessage])

  const onMessageSent = ({ message }) => {
    setNewMessage(message)

    setChats((prevChats) => {
      return processChats(
        prevChats.map((chat) => {
          if (chat.id === message.chatId) {
            return {
              ...chat,
              messages: chat.messages ? [...chat.messages, message] : [message]
            }
          } else {
            return chat
          }
        })
      )
    })
  }

  const onGetUserChats = ({ chats }) => {
    setChats(processChats(chats))
  }

  const onCreateChat = ({ chatWithUsers }) => {
    setChats((prev) => [...prev, chatWithUsers])
  }

  const onGetOnlineChatUsers = ({ users }) => {
    setOnlineUsers(users)
  }

  const onGetOnlinePortalUsers = ({ users }) => {
    setOnlineUsers(users.map((user) => user.userId))
  }

  const onUserConnected = ({ userId }) => {
    if (!onlineUsers.includes(userId)) {
      setOnlineUsers((prev) => [...prev, parseInt(userId)])
    }
  }

  const onUserDisconnected = ({ userId }) => {
    setOnlineUsers((prev) => prev.filter((id) => id !== parseInt(userId)))
  }

  const onUpdateLastSeen = ({ chatId, userId, lastSeen }) => {
    setChats((prevChats) => {
      return processChats(
        prevChats.map((chat) => {
          if (chat.id === chatId) {
            if (userId === me.id) {
              return {
                ...chat,
                lastSeen
              }
            } else {
              return {
                ...chat,
                otherUserLastSeen: lastSeen
              }
            }
          }
          return chat
        })
      )
    })
  }

  const onIsTyping = ({ chatId, senderId }) => {
    if (senderId !== me.id) {
      setChats((prevChats) => {
        return processChats(
          prevChats.map((chat) => {
            if (chat.id === chatId) {
              return {
                ...chat,
                isTypingDate: new Date()
              }
            }
            return chat
          })
        )
      })
    }
  }

  useEffect(() => {
    if (!userId || !portalId || !me) return

    let socketInstance = null

    const initializeSocket = async () => {
      try {
        socketInstance = io.connect(import.meta.env.VITE_CHATVU_BACKEND, {
          transports: ['websocket'],
          auth: {
            token: await getJWT()
          },
          query: {
            userId,
            portalId,
            userName: me.name,
            userEmail: me.email,
            userNumber: me.number,
            applicationId: 'televu-isee'
          }
        })

        setSocket(socketInstance)
        socketRef.current = socketInstance

        if (socketInstance && typeof socketInstance.on === 'function') {
          socketInstance.on('connect_error', (error) => {
            console.error('Connection error:', error)
          })
          socketInstance.on('connect', onConnect)
          socketInstance.on('disconnect', onDisconnect)
          socketInstance.on('chat:getUserChats', onGetUserChats)
          socketInstance.on('chat:sendMessage', onMessageSent)
          socketInstance.on('user:getOnlineChatUsers', onGetOnlineChatUsers)
          socketInstance.on('chat:updateLastSeen', onUpdateLastSeen)
          socketInstance.on('user:connected', onUserConnected)
          socketInstance.on('user:disconnected', onUserDisconnected)
          socketInstance.on('user:getOnlinePortalUsers', onGetOnlinePortalUsers)
          socketInstance.on('chat:chatCreated', onCreateChat)
          socketInstance.on('chat:isTyping', onIsTyping)
          if (typeof socketInstance.onAny === 'function') {
            socketInstance.onAny((eventName, ...args) => {
              // console.log('any', eventName, args)
            })
          }
        }
      } catch (error) {
        console.error('Failed to initialize socket:', error)
      }
    }

    initializeSocket()

    return () => {
      if (pushSubscriptionRef.current) {
        syncPushSubscriptionRef.current?.(
          socketInstance,
          pushSubscriptionRef.current,
          'unregister'
        )
      }
      cleanupSocket(socketInstance)
      socketRef.current = null
      setSocket(null)
    }
  }, [userId, portalId, me])

  // Function to disconnect the chat socket
  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      if (pushSubscriptionRef.current) {
        syncPushSubscriptionRef.current?.(
          socketRef.current,
          pushSubscriptionRef.current,
          'unregister'
        )
      }

      unsubscribeBrowserPushSubscription()
      cleanupSocket(socketRef.current)
      socketRef.current = null
      setSocket(null)
    }
  }, [unsubscribeBrowserPushSubscription])

  return (
    <ChatSocketContext.Provider
      value={{
        socket,
        chats,
        currChatIndex,
        setCurrChatIndex,
        currChatName,
        setCurrChatName,
        onlineUsers,
        setOnlineUsers,
        portalUsers,
        setPortalUsers,
        portalUsersMap,
        setPortalUsersMap,
        isSettingsOpen,
        setIsSettingsOpen,
        loadingChats,
        setLoadingChats,
        disconnectSocket,
        ensurePushSubscription,
        unsubscribeBrowserPushSubscription
      }}
    >
      {props.children}
    </ChatSocketContext.Provider>
  )
}
